import React, { useEffect, useState } from 'react'
import { Typography, Table, Space, Modal, Form, Select, Row, Col } from 'antd'
import { PlusOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { getAllTaxModes, createTaxMode, deleteTaxMode, resetCreateTaxMode } from '../../actions/taxModeAction'
import { getTaxStates } from '../../actions/taxActions'
import { CustomButton } from '../../components/ui/CustomButton'
import { showToast } from '../../utils/toast'
import Colors from '../../utils/colors'
import Constants from '../../utils/constants'
import { removeUnderScoreAndCapitalize } from '../../helpers/strings'

const { Title } = Typography

const CreateTaxMode = () => {
  const dispatch = useDispatch()
  const { loading: statesLoading, states } = useSelector((state) => state.allStates)
  const { loading: taxModesLoading, taxModes, totalTaxModes } = useSelector((state) => state.allTaxModes)
  const { loading: createLoading, isCreated, error: createError } = useSelector((state) => state.createTaxMode)
  const { loading: deleteLoading, isDeleted, error: deleteError } = useSelector((state) => state.deleteTaxMode)
  
  const [currentPage, setCurrentPage] = useState(1)
  const [limit] = useState(Constants.ITEMS_PER_PAGE)
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [selectedTaxModeId, setSelectedTaxModeId] = useState(null)
  const [form] = Form.useForm()

  useEffect(() => {
    dispatch(getTaxStates({ page: 1, mode: Constants.MODES.BORDER_TAX, perPage: 100 }))
      dispatch(getAllTaxModes('', currentPage, limit, { mode: Constants.MODES.BORDER_TAX }))
  }, [dispatch])

  useEffect(() => {
    if (isCreated) {
      showToast('Tax Mode created successfully', 'success')
      setCreateModalVisible(false)
      form.resetFields()
      dispatch(getAllTaxModes('', currentPage, limit, { mode: Constants.MODES.BORDER_TAX }))
      dispatch(resetCreateTaxMode())
    }
    if (createError) {
      showToast(createError, 'error')
    }
  }, [isCreated, createError, dispatch])

  useEffect(() => {
    if (isDeleted) {
      showToast('Tax Mode deleted successfully', 'success')
      setDeleteModalVisible(false)
      setSelectedTaxModeId(null)
      dispatch(getAllTaxModes('', currentPage, limit, { mode: Constants.MODES.BORDER_TAX }))
    }
    if (deleteError) {
      showToast(deleteError, 'error')
    }
  }, [isDeleted, deleteError])

  const columns = [
    {
      title: '#',
      key: 'index',
      render: (_, __, index) => (currentPage - 1) * limit + index + 1,
      width: 50
    },
    {
      title: 'State Name',
      dataIndex: 'state',
      key: 'state',
      render: (state) => removeUnderScoreAndCapitalize(state.name),
      width: 150
    },
    {
      title: 'Tax Mode',
      dataIndex: 'taxMode',
      key: 'taxMode',
      render: (taxMode) => removeUnderScoreAndCapitalize(taxMode),
      width: 120
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <CustomButton 
            size="small"
            onClick={() => handleDelete(record._id)}
            className="delete-btn"
            style={{ 
              height: '28px',
              fontSize: '10px',
              width: 'auto'
            }}
          >
            <DeleteOutlined /> Delete
          </CustomButton>
        </Space>
      ),
      width: 100
    }
  ]

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current)
    dispatch(getAllTaxModes('', { page: pagination.current, limit, filters: {}, mode: Constants.MODES.BORDER_TAX }))
  }

  const handleCreateSubmit = (values) => {
    const taxModeData = {
      ...values,
      mode: Constants.MODES.BORDER_TAX
    }
    dispatch(createTaxMode(taxModeData))
  }

  const handleCreateCancel = () => {
    setCreateModalVisible(false)
    form.resetFields()
  }

  const handleDelete = (taxModeId) => {
    setSelectedTaxModeId(taxModeId)
    setDeleteModalVisible(true)
  }

  const confirmDelete = () => {
    if (selectedTaxModeId) {
      dispatch(deleteTaxMode(selectedTaxModeId))
    }
  }

  const cancelDelete = () => {
    setDeleteModalVisible(false)
    setSelectedTaxModeId(null)
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={4} style={{ color: Colors.primary, margin: 0 }}>
          Border Tax - Tax Modes
        </Title>
        <CustomButton
          type="primary"
          onClick={() => setCreateModalVisible(true)}
          className='create-btn'
          style={{ 
            height: '28px',
            fontSize: '10px',
            width: 'auto'
          }}
        >
          <PlusOutlined /> Create Tax Mode
        </CustomButton>
      </div>
      
      <div style={{ width: '100%', overflowX: 'auto', border: `1px solid ${Colors.darkerGray}`, borderRadius: 10 }}>
        <Table
          columns={columns}
          dataSource={taxModes}
          loading={taxModesLoading}
          rowKey={(record) => record._id}
          pagination={{
            current: currentPage,
            total: totalTaxModes,
            pageSize: limit,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tax modes`,
            itemRender: (current, type, originalElement) => {
              if (type === 'page') {
                return (
                  <span style={{
                    backgroundColor: current === currentPage ? Colors.primary : 'transparent',
                    color: current === currentPage ? Colors.white : Colors.primary,
                    border: `1px solid ${Colors.primary}`,
                    padding: '4px 8px',
                    borderRadius: 4,
                    cursor: 'pointer'
                  }}>
                    {current}
                  </span>
                )
              }
              return originalElement
            }
          }}
          onChange={handleTableChange}
          scroll={{ x: 600 }}
          size="middle"
        />
      </div>

      <Modal
        title="Create Tax Mode"
        open={createModalVisible}
        onOk={() => form.submit()}
        onCancel={handleCreateCancel}
        okText="Create Tax Mode"
        cancelText="Cancel"
        confirmLoading={createLoading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="state"
                label="State"
                rules={[{ required: true, message: 'Please select a state' }]}
              >
                <Select placeholder="Select state">
                  {states?.map(state => (
                    <Select.Option key={state._id} value={state._id}>
                      {removeUnderScoreAndCapitalize(state.name)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="taxMode"
                label="Tax Mode"
                rules={[{ required: true, message: 'Please select tax mode' }]}
              >
                <Select placeholder="Select tax mode">
                  {Object.values(Constants.TAX_MODES || {}).map(mode => (
                    <Select.Option key={mode} value={mode}>
                      {removeUnderScoreAndCapitalize(mode)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Delete Tax Mode"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText="Yes, Delete"
        cancelText="Cancel"
        okType="danger"
        centered
        confirmLoading={deleteLoading}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ExclamationCircleOutlined style={{ color: Colors.warning, fontSize: '22px', marginRight: 12 }} />
          <span>Are you sure you want to delete this tax mode?</span>
        </div>
      </Modal>
    </div>
  )
}

export default CreateTaxMode